import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { SectorBadge } from '@/components/ui/sector-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { User, Filter, X, ExternalLink, Loader2 } from 'lucide-react';

type SectorType = 'mining' | 'processing' | 'infrastructure' | 'industrial' | 'services' | 'other';

interface TalentProfile {
  id: string;
  user_id: string;
  headline: string | null;
  bio: string | null;
  sectors: string[];
  skills: string[];
  languages: string[];
  open_to: string[];
  years_experience: number | null;
  linkedin_url: string | null;
  created_at: string;
  profiles: {
    first_name: string;
    last_name: string;
    country: string | null;
    is_diaspora: boolean;
  };
}

const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;
const OPEN_TO_OPTIONS = ['employment', 'consulting', 'board_roles', 'projects'] as const;
const LANGUAGE_OPTIONS = ['English', 'French', 'Portuguese', 'Swahili', 'Lingala'] as const;

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500',
  'bg-amber-500', 'bg-rose-500', 'bg-teal-500',
];

function getAvatarColor(name: string) {
  const code = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[code];
}

const FLAG_MAP: Record<string, string> = {
  'DR Congo': '🇨🇩', 'United States': '🇺🇸', 'South Africa': '🇿🇦',
  'Canada': '🇨🇦', 'United Kingdom': '🇬🇧', 'France': '🇫🇷',
  'Belgium': '🇧🇪', 'Australia': '🇦🇺', 'China': '🇨🇳', 'Switzerland': '🇨🇭',
};

export default function TalentDirectory() {
  const { t } = useTranslation();
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState<TalentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<TalentProfile | null>(null);

  // Filters
  const [sectorFilters, setSectorFilters] = useState<string[]>([]);
  const [diasporaOnly, setDiasporaOnly] = useState(false);
  const [openToFilters, setOpenToFilters] = useState<string[]>([]);
  const [languageFilters, setLanguageFilters] = useState<string[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }
    if (user && role === 'company') {
      fetchProfessionals();
    }
  }, [user, role, authLoading]);

  const fetchProfessionals = async () => {
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('*, profiles!inner(first_name, last_name, country, is_diaspora)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProfessionals(
        (data || []).map((p: any) => ({
          ...p,
          sectors: Array.isArray(p.sectors) ? p.sectors : [],
          skills: Array.isArray(p.skills) ? p.skills : [],
          languages: Array.isArray(p.languages) ? p.languages : [],
          open_to: Array.isArray(p.open_to) ? p.open_to : [],
          profiles: Array.isArray(p.profiles) ? p.profiles[0] : p.profiles,
        }))
      );
    } catch (error) {
      console.error('Error fetching professionals:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (item: string, arr: string[], setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    setter(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const clearFilters = () => {
    setSectorFilters([]);
    setDiasporaOnly(false);
    setOpenToFilters([]);
    setLanguageFilters([]);
  };

  const hasActiveFilters = sectorFilters.length > 0 || diasporaOnly || openToFilters.length > 0 || languageFilters.length > 0;

  const filtered = professionals.filter(p => {
    if (sectorFilters.length > 0 && !sectorFilters.some(s => p.sectors.includes(s))) return false;
    if (diasporaOnly && !p.profiles.is_diaspora) return false;
    if (openToFilters.length > 0 && !openToFilters.some(o => p.open_to.includes(o))) return false;
    if (languageFilters.length > 0 && !languageFilters.some(l => p.languages.includes(l))) return false;
    return true;
  });

  const FilterPanel = () => (
    <div className="space-y-6">
      {/* Sectors */}
      <div className="space-y-3">
        <Label className="font-semibold">{t('talent.sectors')}</Label>
        <div className="space-y-2">
          {SECTORS.map(s => (
            <Label key={s} className="flex cursor-pointer items-center gap-2">
              <Checkbox checked={sectorFilters.includes(s)} onCheckedChange={() => toggleFilter(s, sectorFilters, setSectorFilters)} />
              <span className="text-sm">{t(`sectors.${s}`)}</span>
            </Label>
          ))}
        </div>
      </div>

      {/* Diaspora */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Switch checked={diasporaOnly} onCheckedChange={setDiasporaOnly} />
          <Label className="text-sm cursor-pointer" onClick={() => setDiasporaOnly(!diasporaOnly)}>
            {t('talent.diasporaOnly')}
          </Label>
        </div>
      </div>

      {/* Open To */}
      <div className="space-y-3">
        <Label className="font-semibold">{t('talent.openTo')}</Label>
        <div className="space-y-2">
          {OPEN_TO_OPTIONS.map(o => (
            <Label key={o} className="flex cursor-pointer items-center gap-2">
              <Checkbox checked={openToFilters.includes(o)} onCheckedChange={() => toggleFilter(o, openToFilters, setOpenToFilters)} />
              <span className="text-sm">{t(`openTo.${o}`)}</span>
            </Label>
          ))}
        </div>
      </div>

      {/* Languages */}
      <div className="space-y-3">
        <Label className="font-semibold">{t('talent.languages')}</Label>
        <div className="space-y-2">
          {LANGUAGE_OPTIONS.map(l => (
            <Label key={l} className="flex cursor-pointer items-center gap-2">
              <Checkbox checked={languageFilters.includes(l)} onCheckedChange={() => toggleFilter(l, languageFilters, setLanguageFilters)} />
              <span className="text-sm">{l}</span>
            </Label>
          ))}
        </div>
      </div>

      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearFilters} className="w-full gap-2">
          <X className="h-4 w-4" /> {t('common.reset')}
        </Button>
      )}
    </div>
  );

  if (authLoading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t('talent.title')}</h1>
            <p className="mt-2 text-muted-foreground">{t('talent.description')}</p>
          </div>
          {/* Mobile filter button */}
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" className="gap-2 lg:hidden">
                <Filter className="h-4 w-4" />
                {t('common.filter')}
                {hasActiveFilters && <Badge variant="secondary">{sectorFilters.length + openToFilters.length + languageFilters.length + (diasporaOnly ? 1 : 0)}</Badge>}
              </Button>
            </SheetTrigger>
            <SheetContent side="left">
              <SheetHeader>
                <SheetTitle>{t('common.filter')}</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterPanel />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex gap-8">
          {/* Desktop filter sidebar */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <Card>
              <CardContent className="p-4">
                <FilterPanel />
              </CardContent>
            </Card>
          </aside>

          {/* Results */}
          <div className="flex-1">
            {loading ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="space-y-2 flex-1">
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                      <Skeleton className="h-4 w-full" />
                      <div className="flex gap-2">
                        <Skeleton className="h-6 w-16 rounded-full" />
                        <Skeleton className="h-6 w-16 rounded-full" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <User className="h-16 w-16 text-muted-foreground/40 mb-4" />
                <p className="text-muted-foreground mb-4">{t('talent.noResults')}</p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>{t('common.reset')}</Button>
                )}
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {filtered.map(p => {
                  const fullName = `${p.profiles.first_name} ${p.profiles.last_name}`;
                  const initials = `${p.profiles.first_name[0]}${p.profiles.last_name[0]}`.toUpperCase();
                  const avatarColor = getAvatarColor(fullName);
                  const visibleSectors = p.sectors.slice(0, 3) as SectorType[];
                  const extraSectors = p.sectors.length - 3;
                  const visibleSkills = p.skills.slice(0, 4);
                  const extraSkills = p.skills.length - 4;
                  const flag = p.profiles.country ? (FLAG_MAP[p.profiles.country] || '🌍') : null;

                  return (
                    <Card key={p.id} className="transition-shadow hover:shadow-lg">
                      <CardContent className="p-6 space-y-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className={`${avatarColor} text-white font-semibold`}>
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-bold truncate">{fullName}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {p.headline || t('talent.noHeadline')}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          {flag && p.profiles.country && (
                            <span className="text-muted-foreground">{flag} {p.profiles.country}</span>
                          )}
                          {p.profiles.is_diaspora && (
                            <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800">
                              🌍 Diaspora
                            </span>
                          )}
                          {p.years_experience != null && (
                            <span className="text-xs text-muted-foreground">{p.years_experience} {t('talent.yearsExp')}</span>
                          )}
                        </div>

                        {visibleSectors.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {visibleSectors.map(s => <SectorBadge key={s} sector={s} showIcon={false} />)}
                            {extraSectors > 0 && <Badge variant="secondary" className="text-xs">+{extraSectors}</Badge>}
                          </div>
                        )}

                        {visibleSkills.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {visibleSkills.map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                            {extraSkills > 0 && <Badge variant="secondary" className="text-xs">+{extraSkills}</Badge>}
                          </div>
                        )}

                        {p.open_to.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {p.open_to.map(o => (
                              <Badge key={o} variant="outline" className="text-xs capitalize">{o.replace('_', ' ')}</Badge>
                            ))}
                          </div>
                        )}

                        <Button variant="outline" className="w-full mt-2" onClick={() => setSelectedProfile(p)}>
                          {t('talent.viewProfile')}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      <Dialog open={!!selectedProfile} onOpenChange={() => setSelectedProfile(null)}>
        {selectedProfile && (() => {
          const p = selectedProfile;
          const fullName = `${p.profiles.first_name} ${p.profiles.last_name}`;
          const initials = `${p.profiles.first_name[0]}${p.profiles.last_name[0]}`.toUpperCase();
          const avatarColor = getAvatarColor(fullName);
          const flag = p.profiles.country ? (FLAG_MAP[p.profiles.country] || '🌍') : null;

          return (
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarFallback className={`${avatarColor} text-white font-bold text-lg`}>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <DialogTitle className="text-xl">{fullName}</DialogTitle>
                    <p className="text-sm text-muted-foreground">{p.headline || t('talent.noHeadline')}</p>
                    {p.profiles.is_diaspora && (
                      <span className="inline-flex items-center rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800 mt-1">
                        🌍 Diaspora
                      </span>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                  {flag && p.profiles.country && <span>{flag} {p.profiles.country}</span>}
                  {p.years_experience != null && <span>{p.years_experience} {t('talent.yearsExp')}</span>}
                </div>

                <div>
                  <Label className="font-semibold text-sm">Bio</Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">
                    {p.bio || t('talent.noBio')}
                  </p>
                </div>

                {p.sectors.length > 0 && (
                  <div>
                    <Label className="font-semibold text-sm">{t('talent.sectors')}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {(p.sectors as SectorType[]).map(s => <SectorBadge key={s} sector={s} />)}
                    </div>
                  </div>
                )}

                {p.skills.length > 0 && (
                  <div>
                    <Label className="font-semibold text-sm">{t('talent.skills')}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {p.skills.map(s => <Badge key={s} variant="secondary">{s}</Badge>)}
                    </div>
                  </div>
                )}

                {p.languages.length > 0 && (
                  <div>
                    <Label className="font-semibold text-sm">{t('talent.languages')}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {p.languages.map(l => <Badge key={l} variant="outline">{l}</Badge>)}
                    </div>
                  </div>
                )}

                {p.open_to.length > 0 && (
                  <div>
                    <Label className="font-semibold text-sm">{t('talent.openTo')}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {p.open_to.map(o => <Badge key={o} variant="outline" className="capitalize">{o.replace('_', ' ')}</Badge>)}
                    </div>
                  </div>
                )}

                {p.linkedin_url && (
                  <a
                    href={p.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View LinkedIn Profile →
                  </a>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedProfile(null)}>
                  {t('common.cancel')}
                </Button>
              </DialogFooter>
            </DialogContent>
          );
        })()}
      </Dialog>
    </Layout>
  );
}
